/**
 * Viewport Culling Utilities
 *
 * Filters sensor data to only include points visible within the current
 * map viewport, with configurable buffer zones for smooth panning.
 *
 * Phase 8.1.1: Viewport-based data culling
 */

import type { SensorDataPoint } from '../MapComponent.types';

// ============================================================================
// Types
// ============================================================================

export interface ViewportBounds {
  /** Western longitude boundary */
  west: number;
  /** Eastern longitude boundary */
  east: number;
  /** Southern latitude boundary */
  south: number;
  /** Northern latitude boundary */
  north: number;
}

export interface CullingOptions {
  /**
   * Buffer zone as fraction of viewport dimensions (0.0 - 1.0)
   * Higher values load more off-screen sensors for smoother panning
   * @default 0.15 (15% buffer)
   */
  buffer?: number;
  /**
   * Minimum number of sensors to return regardless of culling
   * Prevents empty map at far zoom levels
   * @default 0
   */
  minSensors?: number;
  /**
   * Maximum sensors to return (for memory management)
   * @default Infinity
   */
  maxSensors?: number;
}

export interface CullingResult {
  /** Culled sensor data within viewport */
  sensors: SensorDataPoint[];
  /** Number of sensors culled (not visible) */
  culledCount: number;
  /** Total sensors before culling */
  totalCount: number;
  /** Percentage of sensors visible */
  visibilityRatio: number;
  /** Time taken for culling operation (ms) */
  processingTime: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_BUFFER = 0.15; // 15% buffer zone
const MIN_BUFFER = 0.0;
const MAX_BUFFER = 0.5;

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Extract viewport bounds from a MapLibre map reference
 *
 * @param mapBounds - MapLibre LngLatBounds object or bounds array
 * @returns Normalized viewport bounds
 */
export function extractViewportBounds(
  mapBounds: { getWest: () => number; getEast: () => number; getSouth: () => number; getNorth: () => number } | null
): ViewportBounds | null {
  if (!mapBounds) {
    return null;
  }

  return {
    west: mapBounds.getWest(),
    east: mapBounds.getEast(),
    south: mapBounds.getSouth(),
    north: mapBounds.getNorth(),
  };
}

/**
 * Calculate buffered viewport bounds
 *
 * @param bounds - Original viewport bounds
 * @param buffer - Buffer ratio (0.0 - 0.5)
 * @returns Extended bounds with buffer zone
 */
export function calculateBufferedBounds(
  bounds: ViewportBounds,
  buffer: number = DEFAULT_BUFFER
): ViewportBounds {
  const clampedBuffer = Math.min(Math.max(buffer, MIN_BUFFER), MAX_BUFFER);

  const lngSpan = bounds.east - bounds.west;
  const latSpan = bounds.north - bounds.south;

  const lngBuffer = lngSpan * clampedBuffer;
  const latBuffer = latSpan * clampedBuffer;

  return {
    west: bounds.west - lngBuffer,
    east: bounds.east + lngBuffer,
    south: bounds.south - latBuffer,
    north: bounds.north + latBuffer,
  };
}

/**
 * Check if a point is within the given bounds
 *
 * @param lng - Longitude of the point
 * @param lat - Latitude of the point
 * @param bounds - Viewport bounds to check against
 * @returns True if point is within bounds
 */
export function isPointInBounds(
  lng: number,
  lat: number,
  bounds: ViewportBounds
): boolean {
  // Handle wrap-around for longitude
  let adjustedLng = lng;
  if (bounds.west > bounds.east) {
    // Viewport crosses anti-meridian
    if (lng < bounds.west && lng > bounds.east) {
      return false;
    }
  } else {
    if (lng < bounds.west || lng > bounds.east) {
      return false;
    }
  }

  return lat >= bounds.south && lat <= bounds.north;
}

/**
 * Cull sensor data to only include sensors visible in the current viewport
 *
 * This is the main optimization function for large datasets. It filters
 * sensors to only those within the viewport plus a buffer zone, reducing
 * the amount of data that needs to be converted to GeoJSON and rendered.
 *
 * @param sensors - Full array of sensor data points
 * @param bounds - Current viewport bounds (or null for no culling)
 * @param options - Culling configuration options
 * @returns Culling result with visible sensors and statistics
 *
 * @example
 * ```typescript
 * const mapBounds = mapRef.current?.getBounds();
 * const bounds = extractViewportBounds(mapBounds);
 * const result = cullSensorsByViewport(sensorData, bounds, { buffer: 0.2 });
 * console.log(`Showing ${result.sensors.length} of ${result.totalCount} sensors`);
 * ```
 */
export function cullSensorsByViewport(
  sensors: SensorDataPoint[],
  bounds: ViewportBounds | null,
  options: CullingOptions = {}
): CullingResult {
  const startTime = performance.now();

  const {
    buffer = DEFAULT_BUFFER,
    minSensors = 0,
    maxSensors = Infinity,
  } = options;

  // If no bounds provided, return all sensors (no culling)
  if (!bounds) {
    return {
      sensors: sensors.slice(0, maxSensors),
      culledCount: 0,
      totalCount: sensors.length,
      visibilityRatio: 1.0,
      processingTime: performance.now() - startTime,
    };
  }

  // Calculate buffered bounds
  const bufferedBounds = calculateBufferedBounds(bounds, buffer);

  // Filter sensors by viewport
  const visibleSensors: SensorDataPoint[] = [];
  let culledCount = 0;

  for (let i = 0; i < sensors.length && visibleSensors.length < maxSensors; i++) {
    const sensor = sensors[i];
    if (isPointInBounds(sensor.longitude, sensor.latitude, bufferedBounds)) {
      visibleSensors.push(sensor);
    } else {
      culledCount++;
    }
  }

  // Ensure minimum sensor count if specified
  if (visibleSensors.length < minSensors && sensors.length > 0) {
    // Sort remaining sensors by distance from viewport center and add closest ones
    const centerLng = (bounds.west + bounds.east) / 2;
    const centerLat = (bounds.south + bounds.north) / 2;

    const culledSensors = sensors
      .filter((s) => !visibleSensors.includes(s))
      .map((sensor) => ({
        sensor,
        distance: Math.sqrt(
          Math.pow(sensor.longitude - centerLng, 2) +
          Math.pow(sensor.latitude - centerLat, 2)
        ),
      }))
      .sort((a, b) => a.distance - b.distance);

    while (visibleSensors.length < minSensors && culledSensors.length > 0) {
      const closest = culledSensors.shift()!;
      visibleSensors.push(closest.sensor);
      culledCount--;
    }
  }

  const processingTime = performance.now() - startTime;
  const visibilityRatio = sensors.length > 0 ? visibleSensors.length / sensors.length : 1;

  return {
    sensors: visibleSensors,
    culledCount,
    totalCount: sensors.length,
    visibilityRatio,
    processingTime,
  };
}

/**
 * Calculate optimal buffer based on zoom level
 *
 * At low zoom levels (zoomed out), we need less buffer because
 * more of the world is visible. At high zoom levels, we need
 * more buffer for smooth panning.
 *
 * @param zoom - Current map zoom level (0-22)
 * @returns Optimal buffer ratio
 */
export function calculateOptimalBuffer(zoom: number): number {
  // Linear interpolation between zoom levels
  // zoom 0-5: 5% buffer (most of world visible)
  // zoom 5-12: 10-15% buffer
  // zoom 12-18+: 20-25% buffer (detailed view, need smooth panning)

  if (zoom <= 5) {
    return 0.05;
  } else if (zoom <= 12) {
    return 0.05 + ((zoom - 5) / 7) * 0.1; // 5% to 15%
  } else {
    return 0.15 + Math.min((zoom - 12) / 6, 1) * 0.1; // 15% to 25%
  }
}

/**
 * Pre-filter sensors into spatial buckets for faster culling
 *
 * This creates a spatial index for very large datasets (1000+)
 * where linear filtering becomes expensive.
 *
 * @param sensors - Full sensor array
 * @param gridSize - Number of grid cells per axis
 * @returns Spatial grid with sensor buckets
 */
export function createSpatialGrid(
  sensors: SensorDataPoint[],
  gridSize: number = 10
): Map<string, SensorDataPoint[]> {
  const startTime = performance.now();
  const grid = new Map<string, SensorDataPoint[]>();

  // Calculate bounds
  let minLng = Infinity, maxLng = -Infinity;
  let minLat = Infinity, maxLat = -Infinity;

  for (const sensor of sensors) {
    minLng = Math.min(minLng, sensor.longitude);
    maxLng = Math.max(maxLng, sensor.longitude);
    minLat = Math.min(minLat, sensor.latitude);
    maxLat = Math.max(maxLat, sensor.latitude);
  }

  const lngStep = (maxLng - minLng) / gridSize || 1;
  const latStep = (maxLat - minLat) / gridSize || 1;

  // Bucket sensors into grid cells
  for (const sensor of sensors) {
    const cellX = Math.floor((sensor.longitude - minLng) / lngStep);
    const cellY = Math.floor((sensor.latitude - minLat) / latStep);
    const key = `${cellX},${cellY}`;

    if (!grid.has(key)) {
      grid.set(key, []);
    }
    grid.get(key)!.push(sensor);
  }

  return grid;
}

/**
 * Query spatial grid for sensors within bounds
 *
 * @param grid - Spatial grid from createSpatialGrid
 * @param bounds - Query bounds
 * @param gridBounds - Original grid bounds
 * @param gridSize - Grid resolution
 * @returns Sensors within bounds
 */
export function querySpatialGrid(
  grid: Map<string, SensorDataPoint[]>,
  bounds: ViewportBounds,
  gridBounds: { minLng: number; maxLng: number; minLat: number; maxLat: number },
  gridSize: number = 10
): SensorDataPoint[] {
  const startTime = performance.now();
  const results: SensorDataPoint[] = [];

  const lngStep = (gridBounds.maxLng - gridBounds.minLng) / gridSize || 1;
  const latStep = (gridBounds.maxLat - gridBounds.minLat) / gridSize || 1;

  // Calculate which grid cells intersect the query bounds
  const minCellX = Math.max(0, Math.floor((bounds.west - gridBounds.minLng) / lngStep) - 1);
  const maxCellX = Math.min(gridSize, Math.ceil((bounds.east - gridBounds.minLng) / lngStep) + 1);
  const minCellY = Math.max(0, Math.floor((bounds.south - gridBounds.minLat) / latStep) - 1);
  const maxCellY = Math.min(gridSize, Math.ceil((bounds.north - gridBounds.minLat) / latStep) + 1);

  // Query only relevant cells
  for (let x = minCellX; x <= maxCellX; x++) {
    for (let y = minCellY; y <= maxCellY; y++) {
      const key = `${x},${y}`;
      const cellSensors = grid.get(key);
      if (cellSensors) {
        // Fine-grained filtering within cell
        for (const sensor of cellSensors) {
          if (isPointInBounds(sensor.longitude, sensor.latitude, bounds)) {
            results.push(sensor);
          }
        }
      }
    }
  }

  return results;
}

export default {
  extractViewportBounds,
  calculateBufferedBounds,
  isPointInBounds,
  cullSensorsByViewport,
  calculateOptimalBuffer,
  createSpatialGrid,
  querySpatialGrid,
};
